import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  FileTextIcon,
  FilterIcon,
  DownloadIcon,
  SearchIcon,
  UserIcon,
  CalendarIcon,
  ActivityIcon,
  AlertCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EditIcon,
  TrashIcon,
  PlusIcon,
  EyeIcon,
  RefreshCwIcon,
  TrendingUpIcon,
  ClockIcon,
  DatabaseIcon,
  ShieldIcon,
  LogInIcon,
  LogOutIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Action type icons and colors
const ACTION_TYPES = {
  create: { icon: PlusIcon, color: 'text-green-600', bg: 'bg-green-50', label: 'Oluşturma' },
  update: { icon: EditIcon, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Güncelleme' },
  delete: { icon: TrashIcon, color: 'text-red-600', bg: 'bg-red-50', label: 'Silme' },
  view: { icon: EyeIcon, color: 'text-gray-600', bg: 'bg-gray-50', label: 'Görüntüleme' },
  login: { icon: LogInIcon, color: 'text-purple-600', bg: 'bg-purple-50', label: 'Giriş' },
  logout: { icon: LogOutIcon, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Çıkış' },
  approve: { icon: CheckCircleIcon, color: 'text-green-600', bg: 'bg-green-50', label: 'Onaylama' },
  reject: { icon: XCircleIcon, color: 'text-red-600', bg: 'bg-red-50', label: 'Reddetme' },
  payment: { icon: DatabaseIcon, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Ödeme' },
  security: { icon: ShieldIcon, color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'Güvenlik' }
};

const UltraAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    today: 0,
    week: 0,
    month: 0,
    critical: 0
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: '',
    actionType: '',
    user: '',
    resource: ''
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadAuditLogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, logs]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      // Mock data for now - replace with real API call
      const mockLogs = generateMockLogs();
      setLogs(mockLogs);
      calculateStats(mockLogs);
      toast.success('Denetim kayıtları yüklendi');
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Denetim kayıtları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const generateMockLogs = () => {
    const actions = ['create', 'update', 'delete', 'view', 'login', 'logout', 'approve', 'reject', 'payment', 'security'];
    const resources = ['Kullanıcı', 'Kiracı', 'Paket', 'Kampanya', 'Ödeme', 'Ayarlar', 'Özellik', 'Fatura'];
    const users = [
      { name: 'Ultra Admin', email: 'ultra@advancecash.io' },
      { name: 'Demo Company', email: 'demo@quattroflow.com' },
      { name: 'Test User', email: 'test@advancecash.io' }
    ];

    const mockLogs = [];
    for (let i = 0; i < 50; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const resource = resources[Math.floor(Math.random() * resources.length)];
      const user = users[Math.floor(Math.random() * users.length)];
      const daysAgo = Math.floor(Math.random() * 30);
      const timestamp = new Date();
      timestamp.setDate(timestamp.getDate() - daysAgo);

      mockLogs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        action,
        resource,
        resource_id: `res-${Math.floor(Math.random() * 1000)}`,
        user_name: user.name,
        user_email: user.email,
        ip_address: `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64)',
        status: Math.random() > 0.1 ? 'success' : 'error',
        details: `${action === 'create' ? 'Yeni' : ''} ${resource} ${action === 'create' ? 'oluşturuldu' : action === 'update' ? 'güncellendi' : action === 'delete' ? 'silindi' : 'işlemi gerçekleştirildi'}`,
        metadata: {
          changes: action === 'update' ? { field: 'status', old: 'active', new: 'inactive' } : null
        }
      });
    }

    return mockLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const calculateStats = (logsData) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    setStats({
      today: logsData.filter(log => new Date(log.timestamp) >= today).length,
      week: logsData.filter(log => new Date(log.timestamp) >= weekAgo).length,
      month: logsData.filter(log => new Date(log.timestamp) >= monthAgo).length,
      critical: logsData.filter(log => log.status === 'error' || log.action === 'delete' || log.action === 'security').length
    });
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(log =>
        log.user_name.toLowerCase().includes(searchLower) ||
        log.user_email.toLowerCase().includes(searchLower) ||
        log.resource.toLowerCase().includes(searchLower) ||
        log.details.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (filters.startDate) {
      filtered = filtered.filter(log => new Date(log.timestamp) >= new Date(filters.startDate));
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(log => new Date(log.timestamp) <= endDate);
    }

    // Action type filter
    if (filters.actionType) {
      filtered = filtered.filter(log => log.action === filters.actionType);
    }

    // User filter
    if (filters.user) {
      filtered = filtered.filter(log => 
        log.user_email.toLowerCase().includes(filters.user.toLowerCase())
      );
    }

    // Resource filter
    if (filters.resource) {
      filtered = filtered.filter(log => 
        log.resource.toLowerCase().includes(filters.resource.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      startDate: '',
      endDate: '',
      actionType: '',
      user: '',
      resource: ''
    });
  };

  const exportLogs = () => {
    try {
      const csv = [
        ['Zaman', 'Eylem', 'Kaynak', 'Kullanıcı', 'Email', 'IP Adresi', 'Durum', 'Detaylar'].join(','),
        ...filteredLogs.map(log => [
          new Date(log.timestamp).toLocaleString('tr-TR'),
          ACTION_TYPES[log.action]?.label || log.action,
          log.resource,
          log.user_name,
          log.user_email,
          log.ip_address,
          log.status === 'success' ? 'Başarılı' : 'Hata',
          log.details
        ].join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `denetim-kayitlari-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success('Denetim kayıtları dışa aktarıldı');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Dışa aktarma sırasında hata oluştu');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    
    return date.toLocaleDateString('tr-TR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionConfig = (action) => {
    return ACTION_TYPES[action] || { 
      icon: ActivityIcon, 
      color: 'text-gray-600', 
      bg: 'bg-gray-50',
      label: action 
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Denetim Kayıtları</h2>
            <p className="text-gray-600 mt-1">Sistem aktivitelerinin detaylı logları ve izleme</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline" 
              onClick={loadAuditLogs}
              disabled={loading}
            >
              <RefreshCwIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Yenile
            </Button>
            <Button 
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FilterIcon className="w-4 h-4 mr-2" />
              Filtreler
            </Button>
            <Button 
              onClick={exportLogs}
              className="bg-green-600 hover:bg-green-700"
              disabled={filteredLogs.length === 0}
            >
              <DownloadIcon className="w-4 h-4 mr-2" />
              Dışa Aktar
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-6 border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Bugün</p>
              <p className="text-3xl font-bold text-gray-900">{stats.today}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <ActivityIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Son 24 saat aktivite</p>
        </Card>

        <Card className="p-6 border-l-4 border-l-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Bu Hafta</p>
              <p className="text-3xl font-bold text-gray-900">{stats.week}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <TrendingUpIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Son 7 gün aktivite</p>
        </Card>

        <Card className="p-6 border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Bu Ay</p>
              <p className="text-3xl font-bold text-gray-900">{stats.month}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Son 30 gün aktivite</p>
        </Card>

        <Card className="p-6 border-l-4 border-l-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Kritik</p>
              <p className="text-3xl font-bold text-gray-900">{stats.critical}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <AlertCircleIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">Önemli işlemler</p>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Başlangıç Tarihi
              </label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bitiş Tarihi
              </label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Eylem Tipi
              </label>
              <select
                className="w-full h-10 px-3 border border-gray-300 rounded-md"
                value={filters.actionType}
                onChange={(e) => handleFilterChange('actionType', e.target.value)}
              >
                <option value="">Tümü</option>
                {Object.entries(ACTION_TYPES).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kullanıcı Email
              </label>
              <Input
                type="text"
                placeholder="email@example.com"
                value={filters.user}
                onChange={(e) => handleFilterChange('user', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kaynak
              </label>
              <Input
                type="text"
                placeholder="Kullanıcı, Kiracı, vb."
                value={filters.resource}
                onChange={(e) => handleFilterChange('resource', e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                className="w-full"
                onClick={clearFilters}
              >
                Filtreleri Temizle
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Search Bar */}
      <Card className="p-4">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Kullanıcı, kaynak veya işlem ara..."
            className="pl-10 h-12"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
      </Card>

      {/* Logs List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Aktivite Timeline
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredLogs.length} kayıt)
            </span>
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCwIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
            <p className="text-gray-600">Denetim kayıtları yükleniyor...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <FileTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Kayıt bulunamadı</p>
            {(filters.search || filters.startDate || filters.actionType) && (
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                Filtreleri Temizle
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => {
              const actionConfig = getActionConfig(log.action);
              const ActionIcon = actionConfig.icon;

              return (
                <div
                  key={log.id}
                  className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Icon */}
                  <div className={`w-10 h-10 ${actionConfig.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <ActionIcon className={`w-5 h-5 ${actionConfig.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <Badge className={actionConfig.bg + ' ' + actionConfig.color}>
                        {actionConfig.label}
                      </Badge>
                      {log.status === 'error' && (
                        <Badge className="bg-red-100 text-red-700">
                          Hata
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">{formatTimestamp(log.timestamp)}</span>
                    </div>

                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {log.details}
                    </p>

                    <div className="flex items-center space-x-4 text-xs text-gray-600">
                      <span className="flex items-center">
                        <UserIcon className="w-3 h-3 mr-1" />
                        {log.user_name}
                      </span>
                      <span className="flex items-center">
                        <DatabaseIcon className="w-3 h-3 mr-1" />
                        {log.resource}
                      </span>
                      <span className="flex items-center">
                        <ActivityIcon className="w-3 h-3 mr-1" />
                        {log.ip_address}
                      </span>
                    </div>
                  </div>

                  {/* Timestamp */}
                  <div className="text-xs text-gray-500 flex-shrink-0">
                    {new Date(log.timestamp).toLocaleString('tr-TR')}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default UltraAuditLogs;
